// React
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";

// MUI
import { Avatar, IconButton, Button, Tooltip, Typography, TextField, MenuItem, Autocomplete, Dialog, DialogTitle, DialogContent, DialogActions, Alert } from "@mui/material";
import ReplyIcon from '@mui/icons-material/Reply';

// Components
import ChefBusinessCard from './ChefBusinessCard';

// Context
import { useUser } from '../../../UserContext';
import { useAuth } from "../../../AuthContext";
import { useSnackbar } from '../../shared/SnackbarContext';
import { useOrderEvents } from '../../shared/OrderEventsContext';

// Services
import { getKitchenDetails } from '../../../services/kitchenService';
import { createUpdateOrder } from '../../../services/orderService';
import hubWebSocketService from "../../../services/hubWebSocketService";

// App components
import Menu from "../kitchen/Menu";
import OrderDetails from "./OrderDetails";
import PostOrderDetails from "./PostOrderDetails";
import PaymentSetup from "./PaymentSetup";

// Utils, Enums, Models
import { getHubMessageFromJson, getHubNameFromKitchenId } from '../../../utils/hubCommonFunctions';
import { DeliveryMethods } from "../../../enums/DeliveryMethods";
import { OrderItem } from "../../../models/orderItem";
import { MathUtils, Utils } from "../../../utils/utils";
import { validateStreet, validateCity, validateState, validateZipCode, hasAddressErrors } from '../../../utils/addressValidation';
import { US_STATES } from '../../../constants';
import { geocodeAddress, calculateDistance } from '../../../utils/locationUtils';
import { mapskey } from '../../../config/config';

export function formatDateTimeLocal(date) {
  const pad = (value) => String(value).padStart(2, '0');
  const roundedDate = new Date(date);
  const minutes = roundedDate.getMinutes();
  const roundedMinutes = minutes <= 15 ? 0 : minutes <= 45 ? 30 : 0;
  roundedDate.setMinutes(roundedMinutes);
  if (roundedMinutes === 0 && minutes > 45) {
    roundedDate.setHours(roundedDate.getHours() + 1);
    roundedDate.setMinutes(0);
  }
  roundedDate.setSeconds(0, 0);
  return `${roundedDate.getFullYear()}-${pad(roundedDate.getMonth() + 1)}-${pad(roundedDate.getDate())}T${pad(roundedDate.getHours())}:${pad(roundedDate.getMinutes())}`;
}

export function toUtcIsoString(value) {
  if (!value) return value;
  const trimmedValue = value.trim();
  if (trimmedValue.endsWith('Z')) {
    return trimmedValue;
  }

  const normalized = trimmedValue.includes('T')
    ? trimmedValue
    : `${trimmedValue}T00:00`;

  const hasSeconds = normalized.match(/:\d{2}:\d{2}/);
  if (!hasSeconds) {
    return `${normalized}:00Z`;
  }

  return `${normalized}Z`;
}

export default function CreateOrderPage(props) {
    const { user } = useUser();
    const { auth } = useAuth();
    const { kitchenSelected } = useParams();
    const { showMessage } = useSnackbar();
    const { triggerRefreshOrders } = useOrderEvents();

    const [kitchenDetails, setKitchenDetails] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [finalOrder, setFinalOrder] = useState(null);
    const [openConfirmOrder, setOpenConfirmOrder] = useState(false);
    const [selectedDeliveryMethod, setSelectedDeliveryMethod] = useState(null);
    const [deliveryAddress, setDeliveryAddress] = useState({
      street: '',
      city: '',
      state: US_STATES[0].code,
      zipCode: '',
      country: 'USA'
    });
    const [addressErrors, setAddressErrors] = useState({
      street: null,
      city: null,
      state: null,
      zipCode: null
    });
    const [deliveryFee, setDeliveryFee] = useState(0);
    const [deliveryDistanceMiles, setDeliveryDistanceMiles] = useState(0);
    const [isCalculatingDeliveryFee, setIsCalculatingDeliveryFee] = useState(false);
    const [orderReadyBy, setOrderReadyBy] = useState(() => {
      const now = new Date();
      now.setHours(now.getHours() + 2);
      now.setMinutes(Math.ceil(now.getMinutes() / 30) * 30);
      if (now.getMinutes() === 60) {
        now.setHours(now.getHours() + 1);
        now.setMinutes(0);
      }
      const candidate = new Date(now);
      if (candidate.getHours() < 8) {
        candidate.setHours(8, 0, 0, 0);
      } else if (candidate.getHours() > 20 || (candidate.getHours() === 20 && candidate.getMinutes() > 0)) {
        candidate.setDate(candidate.getDate() + 1);
        candidate.setHours(8, 0, 0, 0);
      }
      return formatDateTimeLocal(candidate);
    });
    const customerReady = Utils.isCustomerReady(user);

    useEffect(() => {
      const getMenu = async () => {
          try {
              const responseData = await getKitchenDetails(kitchenSelected, auth);
              setKitchenDetails(responseData);
              
              // Set initial delivery method based on what's available
              if (responseData?.providesDelivery && responseData?.acceptsPickup) {
                setSelectedDeliveryMethod(DeliveryMethods.delivery);
              } else if (responseData?.providesDelivery) {
                setSelectedDeliveryMethod(DeliveryMethods.delivery);
              } else if (responseData?.acceptsPickup) {
                setSelectedDeliveryMethod(DeliveryMethods.pickup);
              }
          } catch (error) {
              setError(error.message);
          } finally {
              setLoading(false);
          }
      };

      getMenu();
    }, [auth, kitchenSelected]);

    useEffect(() => {
      if (finalOrder && finalOrder.items && finalOrder.items.length > 0) {
        const updatedOrder = { ...finalOrder, orderReadyBy: orderReadyBy };
        updatedOrder.deliveryMethod = selectedDeliveryMethod || DeliveryMethods.delivery;
        updatedOrder.deliveryPickupAtAddress = selectedDeliveryMethod === DeliveryMethods.delivery 
          ? deliveryAddress 
          : {
              street: kitchenDetails?.address,
              city: kitchenDetails?.city,
              state: kitchenDetails?.state,
              zipCode: kitchenDetails?.zipCode,
              country: kitchenDetails?.country
            };

        setFinalOrder(updatedOrder);
      }
    }, [deliveryAddress, selectedDeliveryMethod, orderReadyBy, kitchenDetails]);

    // Recalculate delivery fee whenever address or delivery method changes
    useEffect(() => {
      async function recalculateDeliveryFee() {
        if (
          selectedDeliveryMethod !== DeliveryMethods.delivery ||
          !deliveryAddress.street?.trim() ||
          !deliveryAddress.city?.trim() ||
          !deliveryAddress.zipCode?.trim()
        ) {
          setDeliveryFee(0);
          setDeliveryDistanceMiles(0);
          return;
        }

        const chargePerMile = Number(kitchenDetails?.deliveryChargePerMile ?? 0);
        if (chargePerMile === 0) {
          setDeliveryFee(0);
          setDeliveryDistanceMiles(0);
          return;
        }

        setIsCalculatingDeliveryFee(true);
        try {
          const addressString = `${deliveryAddress.street}, ${deliveryAddress.city}, ${deliveryAddress.state} ${deliveryAddress.zipCode}`;
          const coords = await geocodeAddress(addressString, mapskey);
          if (coords && kitchenDetails?.latitude && kitchenDetails?.longitude) {
            const distance = calculateDistance(
              kitchenDetails.latitude, kitchenDetails.longitude,
              coords.latitude, coords.longitude
            );
            const roundedDistance = MathUtils.roundToTwoDecimals(distance);
            setDeliveryDistanceMiles(roundedDistance);
            setDeliveryFee(MathUtils.roundToTwoDecimals(distance * chargePerMile));
          } else {
            setDeliveryFee(0);
            setDeliveryDistanceMiles(0);
          }
        } finally {
          setIsCalculatingDeliveryFee(false);
        }
      }

      recalculateDeliveryFee();
    }, [deliveryAddress, selectedDeliveryMethod, kitchenDetails]);

    // Update order total whenever delivery fee changes
    useEffect(() => {
      if (finalOrder && finalOrder.items?.length > 0) {
        const updated = { ...finalOrder, deliveryFee, deliveryDistanceMiles };
        updated.totalAmount = calculateTotalAmount(updated);
        updated.totalAmountCents = MathUtils.roundToTwoDecimals(updated.totalAmount * 100);
        setFinalOrder(updated);
      }
    }, [deliveryFee, deliveryDistanceMiles]);

    function getKitchenAddressFormatted() {
      return `${kitchenDetails?.address}, ${kitchenDetails?.city}, ${kitchenDetails?.state} ${kitchenDetails?.zipCode}, ${kitchenDetails?.country}`;
    }

    function getKitchenAddressObject() {
      return {
        street: kitchenDetails?.address,
        city: kitchenDetails?.city,
        state: kitchenDetails?.state,
        zipCode: kitchenDetails?.zipCode,
        country: kitchenDetails?.country
      };
    }

    function getDefaultEmptyOrder() {
      return {
        id: 0,
        orderNumber: null,
        customerId: 0,
        kitchenId: 0,
        paymentRef: null,
        transactionId: 0,
        orderDate: null,
        orderReadyBy: null,
        itemsSubTotal: 0,
        deliveryFee: 0,
        deliveryDistanceMiles: 0,
        fees: 0,
        taxes: 0,
        totalAmount: 0,
        totalAmountCents: 0,
        deliveryMethod: null,
        deliveryPickupAtAddress: {
          street: null,
          city: null,
          state: null,
          zipCode: null,
          country: null
        },
        items: [],
        paymentPreference: kitchenDetails.paymentPreference,
        paymentMethod: kitchenDetails.paymentMethod,
        paymentInfo: {
          id: 0,
          creditCardType: null,
          creditCardName: null,
          creditCardNumber: null,
          creditCardExpDate: null,
          creditCardCode: null,
          paymentDate: null,
          billingAddress: {
              street: null,
              city: null,
              state: null,
              zipCode: null,
              country: null
          },
        }};
    }

    function handleAddressChange(field, value) {
      // Update the address value
      setDeliveryAddress(prev => ({
        ...prev,
        [field]: value
      }));

      // Validate the field immediately
      let error = null;
      switch(field) {
        case 'street':
          error = validateStreet(value);
          break;
        case 'city':
          error = validateCity(value);
          break;
        case 'state':
          error = validateState(value);
          break;
        case 'zipCode':
          error = validateZipCode(value);
          break;
        default:
          break;
      }

      // Update the error state
      setAddressErrors(prev => ({
        ...prev,
        [field]: error
      }));
    }

    function handlePlaceOrderClick() {
      setOpenConfirmOrder(true);
    }

    async function handlePlaceOrder(paymentIntent)  {
      setLoading(true);
      try {
          var newOrder = finalOrder;
          newOrder.customerId = user.customer.id;
          newOrder.kitchenId = parseInt(kitchenSelected);
          newOrder.paymentRef = paymentIntent?.id;        // *** paymentIntent will be null until we integrate payment again.
          newOrder.deliveryMethod = selectedDeliveryMethod || DeliveryMethods.delivery;
          newOrder.deliveryPickupAtAddress = selectedDeliveryMethod === DeliveryMethods.delivery ? deliveryAddress : getKitchenAddressObject();
          newOrder.orderReadyBy = toUtcIsoString(orderReadyBy);
          const orderPlaced = await createUpdateOrder(newOrder, auth);
          notifyChef(orderPlaced);

          setFinalOrder(orderPlaced);
          setLoading(false);
      } catch (error) {
          setError(error.message);
      }
    }

    function notifyChef(orderPlaced) {
      if (hubWebSocketService.isConnected()) {
        sendMessageToChef(orderPlaced);
      } else {
        hubWebSocketService.connect({
              clientId: `cus-${orderPlaced.customerId}`,
              token: auth,
              onMessage: (msg) => { 
                const message = getHubMessageFromJson(msg);
                if (message) {
                    showMessage(message, 'info');
                    triggerRefreshOrders();
                }
              },
              onOpen: () => {
                // notify chef after connection is open
                sendMessageToChef(orderPlaced);
              },  
        });
      }
    }

    function sendMessageToChef(orderPlaced) {
      hubWebSocketService.sendMessage(JSON.stringify({
          to: `chef-${orderPlaced.kitchenId}`,
          content: `New order placed: ${orderPlaced.orderNumber} by ${user?.firstName} ${user?.lastName}`
      }));
    }


    function handleMenuItemQuantityChange(menuItemObject) {
      const order = updateOrder(menuItemObject);

      if (itemsInShoppingCart(order.items)) {
        setFinalOrder(order);
      } else {
        setFinalOrder(null);
      }
    }

    function updateOrder(menuItemObject) {
      
      if (kitchenDetails) {
        var order = finalOrder ? { ...finalOrder} : getDefaultEmptyOrder();
        kitchenDetails.menus.forEach(menu => {
          const menuItem = menu.items?.find(item => item.id === menuItemObject.menuItemId);
          if(menuItem) {
            var orderItem = order.items.find(item => item.menuItemId === menuItem.id);
            if (orderItem) {
              orderItem.count = menuItemObject.count;
              if (orderItem.count === 0) {
                order.items.splice(order.items.indexOf(orderItem), 1);
              }
            } else {
              orderItem = {...OrderItem};
              orderItem.menuItemId = menuItem.id;
              orderItem.itemName = menuItem.name;
              orderItem.price = menuItem.price;
              orderItem.count = menuItemObject.count;
              order.items.push(orderItem);
            }
          }
        });

        order.orderDate = new Date();
        order.orderReadyBy = toUtcIsoString(orderReadyBy);
        order.itemsSubTotal = getItemsSubTotal(order.items);
        order.deliveryFee = deliveryFee || 0;
        order.deliveryDistanceMiles = deliveryDistanceMiles || 0;
        // order.fees = calculateFee(order.itemsSubTotal);    // **** skip fee calculation for now, will integrate real fees when we integrate payment again.
        order.totalAmount = calculateTotalAmount(order);
        order.totalAmountCents = MathUtils.roundToTwoDecimals(order.totalAmount * 100);
        return order;
      }

      return {};

    }

    function getItemsSubTotal(orderItems) {
      var sum = 0;
      orderItems.forEach(elem => {
        var value = MathUtils.roundToTwoDecimals(elem.price * elem.count);
        sum += value;
      });

      return sum;
    }

    function calculateTotalAmount(order) {
      var total = order.itemsSubTotal 
      + (order.deliveryFee || 0)
      + order.fees 
      // + order.taxes;
      return MathUtils.roundToTwoDecimals(total);
    }

    const calculateFee = (subtotal) => {
      // Base fee for all orders
      const baseFee = 1.99;
      
      if (subtotal <= 30) {
        return baseFee;
      }
      
      // Calculate $1 for every $20 above $30
      const amountAboveThreshold = subtotal - 30;
      const additionalFee = Math.floor(amountAboveThreshold / 20);
      
      return MathUtils.roundToTwoDecimals(baseFee + additionalFee);
    };
    
    function itemsInShoppingCart(orderItems) {
      return orderItems.length > 0
    }

    function formatPaymentPreference(value) {
      if (value === 'atPickup') {
        return 'Pay at pickup';
      }

      if (value === 'upfront') {
        return 'Upfront';
      }

      return 'Not specified';
    }

    function formatPaymentMethod(value) {
      if (!value || typeof value !== 'string') {
        return 'Not specified';
      }

      const methodLabels = {
        payment_apps: 'Payment Apps',
        cash: 'Cash',
      };

      const uniqueMethods = [...new Set(
        value
          .split(',')
          .map((method) => method.trim())
          .filter(Boolean)
      )];

      if (uniqueMethods.length === 0) {
        return 'Not specified';
      }

      return uniqueMethods
        .map((method) => methodLabels[method] || method)
        .join(', ');
    }
    
      const styles = {
        menuList: {
          listStyleType: 'none',
          padding: 0,
          margin: 0,
        },
        menuItem: {
          fontWeight: 'bold',
        },
        menuPrice: {
          marginLeft: '10px',
        },
      };

      if (error) {
          return <div>Sorry, something went wrong. {error}</div>;
      }

      if (finalOrder && finalOrder.orderNumber) {
        return <div><PostOrderDetails order={finalOrder} /></div>;
      }

      return (
        !loading && kitchenDetails ? (
        <div>
          <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between'}}>
            <div style={{ display: 'flex', flexDirection: 'row', gap:16, alignItems: 'flex-start'}}>
            <Typography variant="h4" gutterBottom>
              {kitchenDetails.name}
            </Typography>
              <ChefBusinessCard chef={kitchenDetails.chef} />
            </div>
            <div>
              <Tooltip title={'Back To List'} placement="top-start" >
                <Link to="/list"><IconButton><ReplyIcon /></IconButton></Link>
              </Tooltip>
            </div>
          </div>

          <div style={{ marginBottom: 12 }}>
            <Typography variant="body2" color="primary" data-testid="kitchen-payment-info">
              {formatPaymentPreference(kitchenDetails.paymentPreference)} ({formatPaymentMethod(kitchenDetails.paymentMethod)})
            </Typography>
          </div>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16, flexWrap: 'wrap', maxWidth: 930}}>
                {kitchenDetails.menus.length > 0 ? (
                  kitchenDetails?.menus?.map(menu => (
                    <Menu key={menu.id} menu={menu} onMenuItemQuantityChange={handleMenuItemQuantityChange}/>
                  ))
                ) : (
                  <div>Loading menu...</div>
                )}
            </div>

            <div style={{ marginTop: 24, marginBottom: 16, padding: 16, border: '1px solid #ddd', borderRadius: 8, backgroundColor: '#f9f9f9' }}>
              <Typography variant="h6" gutterBottom style={{ marginBottom: 8 }}>
                When do you want this order ready?
              </Typography>
              <Typography variant="body2" color="text.secondary" style={{ marginBottom: 12 }}>
                Choose the date and time you’d like your order ready.
              </Typography>

              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <TextField
                  type="date"
                  variant="outlined"
                  value={orderReadyBy ? getDatePart(orderReadyBy) : ''}
                  onChange={(e) => {
                    const datePart = e.target.value;
                    const currentTime = getTimePart(orderReadyBy);
                    const validTime = getValidReadyTimeForDate(datePart, currentTime);
                    setOrderReadyBy(datePart ? `${datePart}T${validTime}` : '');
                  }}
                  inputProps={{
                    min: formatDateTimeLocal(new Date()).split('T')[0],
                  }}
                  sx={{ backgroundColor: '#fff', flex: '1 1 220px' }}
                />

                <TextField
                  select
                  variant="outlined"
                  value={orderReadyBy ? getTimePart(orderReadyBy) : getValidReadyTimeForDate(getDatePart(orderReadyBy), '08:00')}
                  onChange={(e) => {
                    const timePart = e.target.value;
                    const datePart = getDatePart(orderReadyBy);
                    setOrderReadyBy(`${datePart}T${timePart}`);
                  }}
                  sx={{ backgroundColor: '#fff', minWidth: 140, flex: '0 0 160px' }}
                >
                  {getReadyTimeOptionsForDisplay(getDatePart(orderReadyBy)).map((timeOption) => (
                    <MenuItem key={timeOption.value} value={timeOption.value}>
                      {timeOption.label}
                    </MenuItem>
                  ))}
                </TextField>
              </div>
            </div>

            <div style={{ marginTop: 0, marginBottom: 24, padding: 16, border: '1px solid #ddd', borderRadius: 8, backgroundColor: '#f9f9f9' }}>
              <Typography variant="h6" gutterBottom style={{ marginBottom: 16 }}>
                Services offered
              </Typography>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {kitchenDetails.providesDelivery && (
                  <label style={{ display: 'flex', alignItems: 'center', cursor: kitchenDetails.acceptsPickup ? 'pointer' : 'default' }}>
                    <input
                      data-testid="delivery-radio"
                      type="radio"
                      name="deliveryMethod"
                      value={DeliveryMethods.delivery}
                      checked={selectedDeliveryMethod === DeliveryMethods.delivery}
                      onChange={(e) => setSelectedDeliveryMethod(e.target.value)}
                      disabled={!kitchenDetails.acceptsPickup}
                      style={{ marginRight: 8, cursor: kitchenDetails.acceptsPickup ? 'pointer' : 'default' }}
                    />
                    <span style={{ fontSize: '1rem' }}>
                      <strong>Delivery</strong> - Order will be delivered to your address
                    </span>
                  </label>
                )}
                
                {kitchenDetails.acceptsPickup && (
                  <label style={{ display: 'flex', alignItems: 'center', cursor: kitchenDetails.providesDelivery ? 'pointer' : 'default' }}>
                    <input
                      data-testid="pickup-radio"
                      type="radio"
                      name="deliveryMethod"
                      value={DeliveryMethods.pickup}
                      checked={selectedDeliveryMethod === DeliveryMethods.pickup}
                      onChange={(e) => setSelectedDeliveryMethod(e.target.value)}
                      disabled={!kitchenDetails.providesDelivery}
                      style={{ marginRight: 8, cursor: kitchenDetails.providesDelivery ? 'pointer' : 'default' }}
                    />
                    <span style={{ fontSize: '1rem' }}>
                      <strong>Pickup</strong> - You will pick up the order {kitchenDetails.acceptsPickup && selectedDeliveryMethod === DeliveryMethods.pickup && (<span>at <i>{getKitchenAddressFormatted()}</i></span>)}
                    </span>
                  </label>
                  
                )}
              </div>

              {/* Delivery Address Section - Only show when delivery is selected and available */}
              {kitchenDetails.providesDelivery && selectedDeliveryMethod === DeliveryMethods.delivery && (
                <div style={{ marginTop: 24, paddingTop: 24, borderTop: '1px solid #ddd' }}>
                  <Typography variant="subtitle1" gutterBottom style={{ marginBottom: 16, fontWeight: 'bold' }}>
                    Delivery Address
                  </Typography>
                  
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    <TextField
                      label="Street Address"
                      variant="outlined"
                      fullWidth
                      required
                      value={deliveryAddress.street}
                      onChange={(e) => handleAddressChange('street', e.target.value)}
                      placeholder="123 Main Street"
                      error={!!addressErrors.street}
                      helperText={addressErrors.street}
                      inputProps={{ 'data-testid': 'delivery-address-street' }}
                    />
                    
                    <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                      <TextField
                        label="City"
                        variant="outlined"
                        required
                        value={deliveryAddress.city}
                        onChange={(e) => handleAddressChange('city', e.target.value)}
                        placeholder="Miami"
                        style={{ flex: '1 1 200px' }}
                        error={!!addressErrors.city}
                        helperText={addressErrors.city}
                        inputProps={{ 'data-testid': 'delivery-address-city' }}
                      />
                      
                      <TextField
                        label="State"
                        variant="outlined"
                        required
                        value={US_STATES[0].code}
                        style={{ flex: '1 1 100px' }}
                        InputProps={{
                          readOnly: true,
                        }}
                        inputProps={{
                          'data-testid': 'delivery-address-state',
                          maxLength: 2
                        }}
                      />
                      
                      <TextField
                        label="ZIP Code"
                        variant="outlined"
                        required
                        value={deliveryAddress.zipCode}
                        onChange={(e) => handleAddressChange('zipCode', e.target.value)}
                        placeholder="33101"
                        style={{ flex: '1 1 120px' }}
                        error={!!addressErrors.zipCode}
                        helperText={addressErrors.zipCode}
                        inputProps={{ 'data-testid': 'delivery-address-zipCode' }}
                      />
                      
                      <TextField
                        label="Country"
                        variant="outlined"
                        value={deliveryAddress.country}
                        InputProps={{
                          readOnly: true,
                        }}
                        style={{ flex: '1 1 100px' }}
                        inputProps={{ 'data-testid': 'delivery-address-country' }}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div>
              <OrderDetails order={finalOrder} isCheckingOut={false} />
            </div>
            <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'right', marginTop: 32 }}>
                <Button variant="contained"
                  sx={{ textTransform: 'none' }}
                  disabled={!finalOrder || finalOrder.items?.length === 0 ||
                    !customerReady ||
                    (selectedDeliveryMethod === DeliveryMethods.delivery && deliveryAddress.street.trim() === '') ||
                    (selectedDeliveryMethod === DeliveryMethods.delivery && deliveryAddress.city.trim() === '') ||
                    (selectedDeliveryMethod === DeliveryMethods.delivery && deliveryAddress.state.trim() === '') ||
                    (selectedDeliveryMethod === DeliveryMethods.delivery && deliveryAddress.zipCode.trim() === '') ||
                    (selectedDeliveryMethod === DeliveryMethods.delivery && hasAddressErrors(addressErrors))
                  }
                  onClick={handlePlaceOrderClick}>Place Order
                </Button>
            </div>
            {!customerReady && (
            <Alert severity="warning" sx={{ marginTop: 4 }}>
              Please complete your profile information and acknowledge the customer responsibilities in order to place an order. <Link to="/profile" style={{ textDecoration: 'underline' }}>Go to Profile</Link>
            </Alert>)}

            {/* Place Order Confirmation Dialog */}
            <Dialog
              open={openConfirmOrder}
              onClose={() => setOpenConfirmOrder(false)}
              maxWidth="sm"
              fullWidth
              data-testid="place-order-confirmation-dialog"
            >
              <DialogTitle>Confirm Your Order</DialogTitle>
              <DialogContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  You are about to place an order from <strong>{kitchenDetails?.name}</strong>. Please review the items below before confirming.
                </Typography>

                {/* Order items summary */}
                <div style={{ border: '1px solid #e0e0e0', borderRadius: 8, overflow: 'hidden', marginBottom: 16 }}>
                  {/* Header */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 16px', backgroundColor: '#f5f5f5', borderBottom: '1px solid #e0e0e0' }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>Item</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>Subtotal</Typography>
                  </div>
                  {/* Rows */}
                  {finalOrder?.items?.map((item, idx) => (
                    <div
                      key={item.menuItemId}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '10px 16px',
                        borderBottom: idx === finalOrder.items.length - 1 ? 'none' : '1px solid #f0f0f0'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>{item.itemName}</Typography>
                        <Typography variant="caption" color="text.secondary">x{item.count}</Typography>
                      </div>
                      <Typography variant="body2">${(item.price * item.count).toFixed(2)}</Typography>
                    </div>
                  ))}

                  <div style={{ borderTop: '1px solid #e0e0e0', backgroundColor: '#fafafa' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 16px' }}>
                      <Typography variant="body2">Items subtotal</Typography>
                      <Typography variant="body2">${Number(finalOrder?.itemsSubTotal || 0).toFixed(2)}</Typography>
                    </div>

                    {Number(finalOrder?.deliveryFee || 0) > 0 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0 16px 10px' }}>
                        <Typography variant="body2">Delivery fee</Typography>
                        <Typography variant="body2">${Number(finalOrder.deliveryFee).toFixed(2)}</Typography>
                      </div>
                    )}

                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 16px', borderTop: '1px solid #e0e0e0', fontWeight: 700 }}>
                      <Typography variant="body2">Grand total</Typography>
                      <Typography variant="body2">${Number(finalOrder?.totalAmount || 0).toFixed(2)}</Typography>
                    </div>
                  </div>
                </div>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Order Ready By: <strong>{orderReadyBy ? new Date(orderReadyBy).toLocaleString() : 'Not selected'}</strong>
                </Typography>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setOpenConfirmOrder(false)}>Cancel</Button>
                <Button variant="contained" onClick={() => handlePlaceOrder()}>Confirm</Button>
              </DialogActions>
            </Dialog>
        </div>
        ) : (
          <div>Loading...</div>
        )
      );
    }

    export function getDatePart(dateTimeValue) {
      return dateTimeValue ? dateTimeValue.split('T')[0] : formatDateTimeLocal(new Date()).split('T')[0];
    }

    export function getTimePart(dateTimeValue) {
      return dateTimeValue ? dateTimeValue.split('T')[1] : '08:00';
    }

    export function timeToMinutes(timeValue) {
      const [hours, minutes] = timeValue.split(':').map(Number);
      return hours * 60 + minutes;
    }

    export function getEarliestAllowedTimeForDate(datePart) {
      const now = new Date();
      const selectedDate = new Date(`${datePart}T00:00:00`);
      const isToday = selectedDate.toDateString() === now.toDateString();
      if (!isToday) {
        return '08:00';
      }

      const minTime = new Date(now.getTime() + 60 * 60 * 1000);
      const rounded = new Date(minTime);
      rounded.setMinutes(0, 0, 0);

      if (minTime.getMinutes() > 0 && minTime.getMinutes() <= 30) {
        rounded.setMinutes(30);
      } else if (minTime.getMinutes() > 30) {
        rounded.setHours(rounded.getHours() + 1, 0, 0, 0);
      }

      if (rounded.getHours() < 8) {
        return '08:00';
      }

      if (rounded.getHours() > 20 || (rounded.getHours() === 20 && rounded.getMinutes() > 0)) {
        return null;
      }

      return `${String(rounded.getHours()).padStart(2, '0')}:${String(rounded.getMinutes()).padStart(2, '0')}`;
    }

    export function getReadyTimeOptions(datePart) {
      const earliestAllowed = getEarliestAllowedTimeForDate(datePart);
      const options = [];

      for (let hour = 8; hour <= 20; hour++) {
        if (hour === 20) {
          const timeValue = '20:00';
          if (earliestAllowed === null || timeToMinutes(timeValue) >= timeToMinutes(earliestAllowed)) {
            options.push(timeValue);
          }
          continue;
        }

        ['00', '30'].forEach((minute) => {
          const timeValue = `${String(hour).padStart(2, '0')}:${minute}`;
          if (earliestAllowed === null || timeToMinutes(timeValue) >= timeToMinutes(earliestAllowed)) {
            options.push(timeValue);
          }
        });
      }

      return options;
    }

    export function getValidReadyTimeForDate(datePart, currentTime) {
      const options = getReadyTimeOptions(datePart);
      if (!options.length) {
        return '08:00';
      }
      return options.includes(currentTime) ? currentTime : options[0];
    }

    export function getReadyTimeOptionsForDisplay(datePart) {
      return getReadyTimeOptions(datePart).map((timeValue) => ({
        value: timeValue,
        label: formatTimeLabel(timeValue)
      }));
    }

    export function formatTimeLabel(timeValue) {
      if (!timeValue) return '';
      const [hours, minutes] = timeValue.split(':').map(Number);
      const suffix = hours >= 12 ? 'PM' : 'AM';
      const displayHour = hours % 12 === 0 ? 12 : hours % 12;
      return `${displayHour}:${String(minutes).padStart(2, '0')} ${suffix}`;
    }